import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCw } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const brl = (n) => `R$ ${Number(n || 0).toFixed(2).replace(".", ",")}`;

const Kpi = ({ label, value, accent = "text-slate-900" }) => (
  <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
    <p className="text-xs text-slate-400">{label}</p>
    <p className={`mt-1 text-lg font-extrabold ${accent}`}>{value}</p>
  </div>
);

export const RepassesReport = ({ token }) => {
  const [period, setPeriod] = useState("month");
  const [ref, setRef] = useState(new Date().toISOString().slice(0, 7));
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const rp = period === "year" ? String(ref).slice(0, 4) : ref;
      const r = await fetch(`${API}/api/backoffice/repasse?period=${period}&ref=${rp}`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      if (r.ok) setReport(d);
    } catch (e) { toast.error("Falha ao carregar relatório"); } finally { setLoading(false); }
  }, [token, period, ref]);

  useEffect(() => { loadReport(); }, [loadReport]);

  return (
    <div className="space-y-6" data-testid="repasses-report">
      <section className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <h2 className="font-bold text-slate-900">Faturamento por Médico/Clínica</h2>
          <select data-testid="repasse-period" value={period} onChange={(e) => { const p = e.target.value; setPeriod(p); setRef(p === "year" ? String(new Date().getFullYear()) : new Date().toISOString().slice(0, 7)); }} className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs font-semibold">
            <option value="month">Mensal</option><option value="year">Anual</option>
          </select>
          <input data-testid="repasse-ref" type={period === "year" ? "number" : "month"} value={ref} onChange={(e) => setRef(e.target.value)} className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs" />
          <button data-testid="repasse-refresh" onClick={loadReport} className="flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-800"><RefreshCw className="h-3.5 w-3.5" />Atualizar</button>
        </div>
        <p className="mb-3 text-xs text-slate-400">Modelo <b>SaaS flat</b>: a VeloClin <b>não cobra comissão por consulta</b> — o médico fica com 100% do valor. A tabela mostra o faturamento de cada médico/clínica no período (consultas pagas). A receita da VeloClin vem apenas da assinatura mensal.</p>
        {loading ? <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div> : report && (
          <>
            <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Kpi label="Faturamento (consultas pagas)" value={brl(report.totals.gross)} accent="text-emerald-700" />
              <Kpi label="Comissão VeloClin" value="R$ 0,00 (0%)" accent="text-slate-500" />
              <Kpi label="Fica com o médico" value={brl(report.totals.repasse)} accent="text-emerald-700" />
              <Kpi label="Consultas pagas" value={report.totals.count} />
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs text-slate-400"><th className="py-2 pr-3">Médico/Clínica</th><th className="pr-3">Consultas</th><th className="pr-3">Presencial</th><th className="pr-3">Online</th><th className="pr-3">Faturamento</th><th>Fica com o médico</th></tr></thead>
                <tbody>
                  {report.rows.map((r) => (
                    <tr key={r.tenant_id} data-testid={`repasse-row-${r.tenant_id}`} className="border-t border-slate-100">
                      <td className="py-2 pr-3 font-semibold text-slate-800">{r.name}</td>
                      <td className="pr-3">{r.count}</td><td className="pr-3">{r.presencial}</td><td className="pr-3">{r.online}</td>
                      <td className="pr-3 font-semibold text-slate-800">{brl(r.gross)}</td><td className="font-semibold text-emerald-700">{brl(r.repasse)}</td>
                    </tr>
                  ))}
                  {report.rows.length === 0 && <tr><td colSpan={6} className="py-4 text-center text-slate-400">Sem consultas pagas no período.</td></tr>}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>
    </div>
  );
};
